import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Close as CloseIcon, History as HistoryIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import { mixed, number, object, string } from 'yup';
import AuditHistoryDrawer from './AuditHistoryDrawer';
import type { AdminCatalogOption, AdminProductSearchItem } from '../types/adminInventoryAdd';
import {
  createAdminProduct,
  createProductBrand,
  createProductCategory,
  createProductModel,
  fetchProductBrands,
  fetchProductCategories,
  searchProductModels,
} from '../api/adminInventoryAdd';
import { fetchInventoryProductForEdit, updateInventoryProduct } from '../api/inventoryAdmin';
import type {
  InventoryExtraPhoto,
  InventoryLegacyPhoto,
  InventoryProductPhoto,
} from '../api/inventoryAdmin';
import InventoryPhotosPanel, { buildProductPhotoSlots } from './InventoryPhotosPanel';
import ProductSalesChannelsPanel from './ProductSalesChannelsPanel';

type CreateOptionKind = 'brand' | 'category' | 'model';
const ACCENT = '#000000';
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const formFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(248, 250, 252, 1)',
    '& fieldset': { borderColor: 'rgba(219, 224, 230, 1)' },
    '&:hover fieldset': { borderColor: 'rgba(148, 163, 184, 0.7)' },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${ACCENT}26`,
      '& fieldset': { borderColor: ACCENT },
    },
  },
};

interface CreateCatalogOption {
  kind: CreateOptionKind;
  id: string;
  label: string;
  query: string;
}

type SelectOption = AdminCatalogOption | CreateCatalogOption;

interface CreateProductFormValues {
  brandId: number | null;
  categoryId: number | null;
  modelId: number | null;
  colorway: string;
  sku: string;
  description: string;
  imageFile: File | null;
}

interface AdminCreateProductModalProps {
  open: boolean;
  mode?: 'create' | 'edit';
  editInventoryId?: number | null;
  initialModelQuery?: string;
  onClose: () => void;
  onCreated?: (product: AdminProductSearchItem) => void;
  onUpdated?: () => void;
}

const schema = object({
  brandId: number().required('La marca es obligatoria').typeError('La marca es obligatoria'),
  categoryId: number()
    .required('La categoría es obligatoria')
    .typeError('La categoría es obligatoria'),
  modelId: number().required('El modelo es obligatorio').typeError('El modelo es obligatorio'),
  colorway: string().trim().required('El colorway es obligatorio'),
  sku: string().trim().required('El SKU es obligatorio'),
  description: string().trim(),
  imageFile: mixed<File>().nullable(),
});

function isCreateOption(option: SelectOption | null | undefined): option is CreateCatalogOption {
  return Boolean(option && 'kind' in option);
}

function toTitleCase(text: string): string {
  return text
    .split(' ')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

const CREATE_KIND_LABELS: Record<CreateOptionKind, string> = {
  brand: '+ Crear marca',
  category: '+ Crear categoría',
  model: '+ Crear modelo',
};

function buildCreateOption(kind: CreateOptionKind, query: string): CreateCatalogOption {
  return {
    kind,
    id: `__create_${kind}__`,
    label: `${CREATE_KIND_LABELS[kind]}: ${query}`,
    query,
  };
}

interface PendingCreate {
  kind: CreateOptionKind;
  name: string;
}

const CONFIRM_ENTITY_LABELS: Record<CreateOptionKind, string> = {
  brand: 'marca',
  category: 'categoría',
  model: 'modelo',
};

function renderAutocompleteInput(
  params: AutocompleteRenderInputParams,
  cfg: { label: string; placeholder: string; error?: boolean; helperText?: string }
): React.ReactElement {
  return (
    <TextField
      id={params.id}
      disabled={params.disabled}
      fullWidth={params.fullWidth}
      size={params.size}
      label={cfg.label}
      placeholder={cfg.placeholder}
      error={cfg.error}
      helperText={cfg.helperText}
      sx={formFieldSx}
      slotProps={{
        inputLabel: params.InputLabelProps,
        htmlInput: params.inputProps,
        input: {
          ref: params.InputProps.ref,
          className: params.InputProps.className,
          startAdornment: params.InputProps.startAdornment,
          endAdornment: params.InputProps.endAdornment,
        },
      }}
    />
  );
}

function AdminCreateProductModal({
  open,
  mode = 'create',
  editInventoryId = null,
  initialModelQuery = '',
  onClose,
  onCreated,
  onUpdated,
}: AdminCreateProductModalProps): React.ReactElement {
  const [brands, setBrands] = useState<AdminCatalogOption[]>([]);
  const [categories, setCategories] = useState<AdminCatalogOption[]>([]);
  const [models, setModels] = useState<AdminCatalogOption[]>([]);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [submittingError, setSubmittingError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [brandInput, setBrandInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [modelInput, setModelInput] = useState('');
  const [pendingCreate, setPendingCreate] = useState<PendingCreate | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [editProductId, setEditProductId] = useState<number | null>(null);
  const [editPhotos, setEditPhotos] = useState<InventoryProductPhoto[]>([]);
  const [editExtraPhotos, setEditExtraPhotos] = useState<InventoryExtraPhoto[]>([]);
  const [editLegacyPhotos, setEditLegacyPhotos] = useState<InventoryLegacyPhoto[]>([]);
  const [editThumbnailUrl, setEditThumbnailUrl] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [channelsRefreshKey, setChannelsRefreshKey] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const formik = useFormik<CreateProductFormValues>({
    initialValues: {
      brandId: null,
      categoryId: null,
      modelId: null,
      colorway: '',
      sku: '',
      description: '',
      imageFile: null,
    },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmittingError(null);
      if (mode === 'edit') {
        if (!editInventoryId) {
          setSubmittingError('No se identificó el producto a editar.');
          setSubmitting(false);
          return;
        }
        const result = await updateInventoryProduct(editInventoryId, {
          brandId: values.brandId as number,
          categoryId: values.categoryId as number,
          modelId: values.modelId as number,
          colorway: values.colorway,
          sku: values.sku,
          description: values.description,
        });
        setSubmitting(false);
        if (!result.ok) {
          setSubmittingError(result.error);
          return;
        }
        onUpdated?.();
        return;
      }
      const result = await createAdminProduct({
        brandId: values.brandId as number,
        categoryId: values.categoryId as number,
        modelId: values.modelId as number,
        colorway: values.colorway,
        sku: values.sku,
        description: values.description,
        image: values.imageFile,
      });
      setSubmitting(false);
      if (!result.ok) {
        setSubmittingError(result.error);
        return;
      }
      onCreated?.(result.product);
    },
  });

  useEffect(() => {
    if (!open) return () => {};
    let cancelled = false;
    setLoadingDeps(true);
    setSubmittingError(null);
    setUploadError(null);
    setExistingImageUrl(null);
    setEditPhotos([]);
    setEditExtraPhotos([]);
    setEditLegacyPhotos([]);
    setEditThumbnailUrl(null);
    setEditProductId(null);
    formik.resetForm();
    setBrandInput('');
    setCategoryInput('');
    setModelInput(initialModelQuery);
    (async () => {
      const [brandItems, categoryItems] = await Promise.all([
        fetchProductBrands(),
        fetchProductCategories(),
      ]);
      if (cancelled) return;
      setBrands(brandItems);
      setCategories(categoryItems);
      if (mode === 'edit' && editInventoryId) {
        setLoadingEditData(true);
        const editData = await fetchInventoryProductForEdit(editInventoryId);
        if (cancelled) return;
        setLoadingEditData(false);
        if (!editData.ok) {
          setSubmittingError(editData.error);
          setLoadingDeps(false);
          return;
        }
        formik.setValues({
          brandId: editData.data.brandId,
          categoryId: editData.data.categoryId,
          modelId: editData.data.modelId,
          colorway: editData.data.colorway,
          sku: editData.data.sku,
          description: editData.data.description,
          imageFile: null,
        });
        setBrandInput(brandItems.find((x) => x.id === editData.data.brandId)?.label ?? '');
        setCategoryInput(categoryItems.find((x) => x.id === editData.data.categoryId)?.label ?? '');
        if (editData.data.modelLabel) {
          setModelInput(editData.data.modelLabel);
          setModels((prev) =>
            prev.some((x) => x.id === editData.data.modelId)
              ? prev
              : [...prev, { id: editData.data.modelId, label: editData.data.modelLabel }]
          );
        } else {
          setModelInput('');
        }
        setExistingImageUrl(editData.data.imageUrl);
        setEditPhotos(editData.data.photos);
        setEditExtraPhotos(editData.data.extraPhotos);
        setEditLegacyPhotos(editData.data.legacyPhotos);
        setEditThumbnailUrl(editData.data.thumbnailUrl);
        setEditProductId(editData.data.productId);
        setChannelsRefreshKey((k) => k + 1);
      }
      setLoadingDeps(false);
    })();
    return () => {
      cancelled = true;
    };
    // `formik` object identity changes often; we only need reset on modal open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialModelQuery, mode, editInventoryId]);

  useEffect(() => {
    if (!open) return () => {};
    if (!formik.values.brandId || !formik.values.categoryId) {
      setModels([]);
      setLoadingModels(false);
      return () => {};
    }
    let cancelled = false;
    setLoadingModels(true);
    const timer = setTimeout(async () => {
      const items = await searchProductModels({
        q: modelInput,
        brandId: formik.values.brandId as number,
        categoryId: formik.values.categoryId as number,
      });
      if (cancelled) return;
      setModels(items);
      setLoadingModels(false);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, modelInput, formik.values.brandId, formik.values.categoryId]);

  const reloadEditPhotos = useCallback(async () => {
    if (mode !== 'edit' || !editInventoryId) return;
    const editData = await fetchInventoryProductForEdit(editInventoryId);
    if (!editData.ok) return;
    setEditPhotos(editData.data.photos);
    setEditExtraPhotos(editData.data.extraPhotos);
    setEditLegacyPhotos(editData.data.legacyPhotos);
    setEditThumbnailUrl(editData.data.thumbnailUrl);
    setExistingImageUrl(editData.data.imageUrl);
  }, [editInventoryId, mode]);

  const handleCancelCreate = () => setPendingCreate(null);

  const handleConfirmCreate = async () => {
    if (!pendingCreate) return;
    const { kind, name } = pendingCreate;
    setPendingCreate(null);

    if (kind === 'brand') {
      const created = await createProductBrand(name);
      if (!created) return;
      setBrands((prev) => (prev.some((x) => x.id === created.id) ? prev : [...prev, created]));
      formik.setFieldValue('brandId', created.id);
      formik.setFieldValue('modelId', null);
      setBrandInput(created.label);
      setModelInput('');
    } else if (kind === 'category') {
      const created = await createProductCategory(name);
      if (!created) return;
      setCategories((prev) => (prev.some((x) => x.id === created.id) ? prev : [...prev, created]));
      formik.setFieldValue('categoryId', created.id);
      formik.setFieldValue('modelId', null);
      setCategoryInput(created.label);
      setModelInput('');
    } else if (kind === 'model') {
      const created = await createProductModel({
        name,
        brandId: formik.values.brandId as number,
        categoryId: formik.values.categoryId as number,
      });
      if (!created) return;
      setModels((prev) => (prev.some((x) => x.id === created.id) ? prev : [...prev, created]));
      formik.setFieldValue('modelId', created.id);
      setModelInput(created.label);
    }
  };

  const imagePreviewUrl = useMemo(() => {
    if (!formik.values.imageFile) return null;
    return URL.createObjectURL(formik.values.imageFile);
  }, [formik.values.imageFile]);

  useEffect(
    () => () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    },
    [imagePreviewUrl]
  );

  const brandOptions: SelectOption[] = (() => {
    const q = brandInput.trim();
    const exists = brands.some((x) => x.label.toLowerCase() === q.toLowerCase());
    if (!q || exists) return brands;
    return [...brands, buildCreateOption('brand', q)];
  })();

  const categoryOptions: SelectOption[] = (() => {
    const q = categoryInput.trim();
    const exists = categories.some((x) => x.label.toLowerCase() === q.toLowerCase());
    if (!q || exists) return categories;
    return [...categories, buildCreateOption('category', q)];
  })();

  const modelOptions: SelectOption[] = (() => {
    const q = modelInput.trim();
    const exists = models.some((x) => x.label.toLowerCase() === q.toLowerCase());
    if (!q || exists || !formik.values.brandId || !formik.values.categoryId) return models;
    return [...models, buildCreateOption('model', q)];
  })();

  const handleImageFile = (file: File | null) => {
    if (!file) {
      setUploadError(null);
      formik.setFieldValue('imageFile', null);
      return;
    }
    if (!ACCEPT_IMAGE_TYPES.includes(file.type)) {
      setUploadError('Formato no válido. Usa PNG, JPG o WEBP.');
      formik.setFieldValue('imageFile', null);
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setUploadError('La imagen no debe superar 5MB.');
      formik.setFieldValue('imageFile', null);
      return;
    }
    setUploadError(null);
    formik.setFieldValue('imageFile', file);
  };

  const onImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0] ?? null;
    handleImageFile(file);
    e.target.value = '';
  };

  const onImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingImage(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleImageFile(file);
  };

  const onImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingImage(true);
  };

  const onImageDragLeave = () => {
    setIsDraggingImage(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 28px 80px rgba(15, 23, 42, 0.28)',
        },
      }}
    >
      <DialogTitle sx={{ pr: 7, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {mode === 'edit' ? 'Editar producto' : 'Nuevo producto'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {mode === 'edit'
            ? 'Actualiza la información global del producto seleccionado.'
            : 'Completa la información global del producto para crearlo y seleccionarlo en la fila activa.'}
        </Typography>
        <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 0.5 }}>
          {mode === 'edit' && editProductId != null && (
            <IconButton
              aria-label="Ver historial del producto"
              onClick={() => setHistoryOpen(true)}
              size="small"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton aria-label="Cerrar modal" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#f8fafc', p: { xs: 2, md: 3 } }}>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
              gap: 2.5,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Autocomplete<SelectOption, false, false, false>
                options={brandOptions}
                value={brands.find((b) => b.id === formik.values.brandId) ?? null}
                inputValue={brandInput}
                onInputChange={(_, v) => setBrandInput(v)}
                onChange={(_, value) => {
                  if (isCreateOption(value) && value.kind === 'brand') {
                    setPendingCreate({ kind: 'brand', name: toTitleCase(value.query) });
                    return;
                  }
                  formik.setFieldValue(
                    'brandId',
                    isCreateOption(value) ? null : (value?.id ?? null)
                  );
                  formik.setFieldValue('modelId', null);
                  setModelInput('');
                }}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                loading={loadingDeps}
                renderInput={(params) =>
                  renderAutocompleteInput(params, {
                    label: 'Marca',
                    placeholder: 'Selecciona o crea marca',
                    error: formik.touched.brandId && Boolean(formik.errors.brandId),
                    helperText: formik.touched.brandId ? formik.errors.brandId : undefined,
                  })
                }
              />

              <Autocomplete<SelectOption, false, false, false>
                options={categoryOptions}
                value={categories.find((c) => c.id === formik.values.categoryId) ?? null}
                inputValue={categoryInput}
                onInputChange={(_, v) => setCategoryInput(v)}
                onChange={(_, value) => {
                  if (isCreateOption(value) && value.kind === 'category') {
                    setPendingCreate({ kind: 'category', name: toTitleCase(value.query) });
                    return;
                  }
                  formik.setFieldValue(
                    'categoryId',
                    isCreateOption(value) ? null : (value?.id ?? null)
                  );
                  formik.setFieldValue('modelId', null);
                  setModelInput('');
                }}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                loading={loadingDeps}
                renderInput={(params) =>
                  renderAutocompleteInput(params, {
                    label: 'Categoría',
                    placeholder: 'Selecciona o crea categoría',
                    error: formik.touched.categoryId && Boolean(formik.errors.categoryId),
                    helperText: formik.touched.categoryId ? formik.errors.categoryId : undefined,
                  })
                }
              />

              <Autocomplete<SelectOption, false, false, false>
                options={modelOptions}
                value={models.find((m) => m.id === formik.values.modelId) ?? null}
                inputValue={modelInput}
                disabled={!formik.values.brandId || !formik.values.categoryId}
                onInputChange={(_, v) => setModelInput(v)}
                onChange={(_, value) => {
                  if (isCreateOption(value) && value.kind === 'model') {
                    setPendingCreate({ kind: 'model', name: toTitleCase(value.query) });
                    return;
                  }
                  formik.setFieldValue(
                    'modelId',
                    isCreateOption(value) ? null : (value?.id ?? null)
                  );
                }}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                loading={loadingModels}
                renderInput={(params) =>
                  renderAutocompleteInput(params, {
                    label: 'Modelo',
                    placeholder: 'Buscar o crear modelo',
                    error: formik.touched.modelId && Boolean(formik.errors.modelId),
                    helperText: formik.touched.modelId ? formik.errors.modelId : undefined,
                  })
                }
              />

              <TextField
                name="colorway"
                label="Colorway"
                value={formik.values.colorway}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={formFieldSx}
                error={formik.touched.colorway && Boolean(formik.errors.colorway)}
                helperText={formik.touched.colorway ? formik.errors.colorway : undefined}
              />
              <TextField
                name="sku"
                label="SKU"
                value={formik.values.sku}
                onChange={(e) => formik.setFieldValue('sku', e.target.value.toUpperCase())}
                onBlur={formik.handleBlur}
                sx={formFieldSx}
                error={formik.touched.sku && Boolean(formik.errors.sku)}
                helperText={formik.touched.sku ? formik.errors.sku : undefined}
              />
              <TextField
                name="description"
                label="Descripción (opcional)"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                multiline
                minRows={4}
                sx={{ ...formFieldSx, gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description ? formik.errors.description : undefined}
              />
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {mode === 'edit' ? 'Fotos del producto' : 'Foto principal'}
              </Typography>
              {mode === 'edit' && editInventoryId ? (
                <InventoryPhotosPanel
                  inventoryId={editInventoryId}
                  scope="product"
                  requiredSlots={buildProductPhotoSlots(editPhotos, editThumbnailUrl)}
                  extraPhotos={editExtraPhotos}
                  legacyPhotos={editLegacyPhotos}
                  onUpdated={() => {
                    reloadEditPhotos().catch(() => {});
                    setChannelsRefreshKey((k) => k + 1);
                  }}
                />
              ) : (
                <>
                  <input
                    ref={imageInputRef}
                    type="file"
                    hidden
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={onImageInputChange}
                  />
                  <Box
                    onClick={() => imageInputRef.current?.click()}
                    onDrop={onImageDrop}
                    onDragOver={onImageDragOver}
                    onDragLeave={onImageDragLeave}
                    sx={{
                      cursor: 'pointer',
                      mt: 0.25,
                      width: '100%',
                      minHeight: 250,
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: isDraggingImage ? ACCENT : 'divider',
                      bgcolor: isDraggingImage
                        ? 'rgba(15, 23, 42, 0.06)'
                        : 'rgba(241, 245, 249, 0.55)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      px: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: ACCENT,
                        bgcolor: 'rgba(15, 23, 42, 0.06)',
                      },
                    }}
                  >
                    {imagePreviewUrl || existingImageUrl ? (
                      <Box
                        component="img"
                        src={imagePreviewUrl || (existingImageUrl as string)}
                        alt="Vista previa del producto"
                        sx={{ width: '100%', maxHeight: 260, objectFit: 'contain' }}
                      />
                    ) : (
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Haz clic o arrastra una imagen
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PNG, JPG o WEBP hasta 5MB
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {uploadError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {uploadError}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Imagen opcional (misma lógica de carga visual que INE).
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          {mode === 'edit' && editInventoryId ? (
            <ProductSalesChannelsPanel
              key={`${editInventoryId}-${channelsRefreshKey}`}
              inventoryId={editInventoryId}
            />
          ) : null}
          {submittingError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submittingError}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(248, 250, 252, 1)',
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          disabled={formik.isSubmitting || loadingEditData}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 2.5,
            bgcolor: ACCENT,
            '&:hover': { bgcolor: '#1e293b' },
          }}
        >
          {formik.isSubmitting || loadingEditData ? (
            <CircularProgress size={18} color="inherit" />
          ) : null}
          {!formik.isSubmitting && !loadingEditData && mode === 'edit' ? 'Guardar cambios' : null}
          {!formik.isSubmitting && !loadingEditData && mode !== 'edit' ? 'Crear producto' : null}
        </Button>
      </DialogActions>

      <AuditHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        mode="product"
        entityId={editProductId}
        entityTitle={
          formik.values.modelId != null ? formik.values.colorway || undefined : undefined
        }
      />

      <Dialog open={Boolean(pendingCreate)} onClose={handleCancelCreate} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar creación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Deseas crear {pendingCreate ? CONFIRM_ENTITY_LABELS[pendingCreate.kind] : ''}{' '}
            <strong>&ldquo;{pendingCreate?.name}&rdquo;</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelCreate} color="inherit" sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmCreate}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#1e293b' },
            }}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default AdminCreateProductModal;

AdminCreateProductModal.defaultProps = {
  mode: 'create',
  editInventoryId: null,
  initialModelQuery: '',
  onCreated: undefined,
  onUpdated: undefined,
};
